import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import { FaPlus, FaTrash, FaChevronDown, FaChevronRight, FaRegClone } from 'react-icons/fa';

const TipsEditor = ({ data = [], activeLang, availableLangObj, onUpdate }) => {
    const [expandedSections, setExpandedSections] = useState({});

    // Toggle section expansion
    const toggleSection = (index) => {
        setExpandedSections(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // Add a new tip section
    const addTipSection = () => {
        const newSection = {
            header: { ...availableLangObj },
            subHeader: { ...availableLangObj },
            tipsArray: [
                {
                    text: { ...availableLangObj },
                    svg: ''
                }
            ]
        };
        onUpdate([...data, newSection]);
        setExpandedSections(prev => ({ ...prev, [data.length]: true }));
    };

    // Remove a tip section
    const removeTipSection = (sectionIndex) => {
        if (window.confirm("Are you sure you want to remove this section?")) {
            const newData = data.filter((_, idx) => idx !== sectionIndex);

            // Reindex expandedSections: remove the deleted index and shift down higher indices
            const newExpandedSections = {};
            Object.keys(expandedSections).forEach(key => {
                const idx = parseInt(key, 10);
                if (idx < sectionIndex) {
                    // Keep indices below the removed one
                    newExpandedSections[idx] = expandedSections[key];
                } else if (idx > sectionIndex) {
                    // Shift down indices above the removed one
                    newExpandedSections[idx - 1] = expandedSections[key];
                }
                // Skip the removed index itself
            });
            setExpandedSections(newExpandedSections);

            onUpdate(newData);
        }
    };

    // Update section header
    const updateSectionHeader = (sectionIndex, value) => {
        const newData = [...data];
        newData[sectionIndex] = {
            ...newData[sectionIndex],
            header: {
                ...newData[sectionIndex].header,
                [activeLang]: value
            }
        };
        onUpdate(newData);
    };

    // Update section subHeader
    const updateSectionSubHeader = (sectionIndex, value) => {
        const newData = [...data];
        newData[sectionIndex] = {
            ...newData[sectionIndex],
            subHeader: {
                ...newData[sectionIndex].subHeader,
                [activeLang]: value
            }
        };
        onUpdate(newData);
    };

    // Add a new tip to a section
    const addTip = (sectionIndex) => {
        const newTip = { text: { ...availableLangObj }, svg: '' };
        const newData = data.map((section, idx) =>
            idx === sectionIndex
                ? { ...section, tipsArray: [...section.tipsArray, newTip] }
                : section
        );
        onUpdate(newData);
    };

    // Remove a tip from a section
    const removeTip = (sectionIndex, tipIndex) => {
        const newData = data.map((section, idx) =>
            idx === sectionIndex
                ? { ...section, tipsArray: section.tipsArray.filter((_, tIdx) => tIdx !== tipIndex) }
                : section
        );
        onUpdate(newData);
    };

    // Update tip text
    const updateTipText = (sectionIndex, tipIndex, value) => {
        const newData = [...data];
        newData[sectionIndex].tipsArray[tipIndex] = {
            ...newData[sectionIndex].tipsArray[tipIndex],
            text: {
                ...newData[sectionIndex].tipsArray[tipIndex].text,
                [activeLang]: value
            }
        };
        onUpdate(newData);
    };

    // Update tip SVG
    const updateTipSvg = (sectionIndex, tipIndex, value) => {
        const newData = [...data];
        newData[sectionIndex].tipsArray[tipIndex] = {
            ...newData[sectionIndex].tipsArray[tipIndex],
            svg: value
        };
        onUpdate(newData);
    };

    return (
        <div className="pfc-tips-container">
            {/* Header */}
            <div className="pfc-grid-header">
                <strong>{data.length}</strong> Section{data.length !== 1 && 's'} Found
            </div>

            {/* Sections List */}
            {data.map((section, sectionIndex) => (
                <div key={sectionIndex} className="pfc-tips-section-card">
                    {/* Card Header Row */}
                    <div className="pfc-section-header-row">
                        <div className="pfc-section-title-group">
                            <span className="pfc-section-badge">Section {sectionIndex + 1}</span>
                            <div className="pfc-section-preview-title">
                                {section.header?.[activeLang] || <span style={{ opacity: 0.5 }}>Untitled Section</span>}
                            </div>
                        </div>
                        <button
                            className="pfc-btn-icon-danger"
                            title="Remove Section"
                            onClick={() => removeTipSection(sectionIndex)}
                        >
                            <FaTrash size={12} />
                        </button>
                    </div>

                    {/* Section Content */}
                    <div className="pfc-section-form">
                        <div className="pfc-form-group">
                            <label className="pfc-tip-input-label">Section Header</label>
                            <Form.Control
                                type="text"
                                className="pfc-input"
                                placeholder="Enter header text..."
                                value={section.header?.[activeLang] || ''}
                                onChange={(e) => updateSectionHeader(sectionIndex, e.target.value)}
                            />
                        </div>

                        {section.subHeader && (
                            <div className="pfc-form-group">
                                <label className="pfc-tip-input-label">Sub Header</label>
                                <Form.Control
                                    type="text"
                                    className="pfc-input"
                                    placeholder="Enter sub-header text..."
                                    value={section.subHeader?.[activeLang] || ''}
                                    onChange={(e) => updateSectionSubHeader(sectionIndex, e.target.value)}
                                />
                            </div>
                        )}

                        <div className="pfc-tips-wrapper">
                            <div className="pfc-section-header-row" style={{ border: 'none', margin: '1rem 0 0.5rem 0', padding: 0 }}>
                                <label className="pfc-tip-input-label">Tips List</label>
                                <button className="pfc-btn-add-tip" onClick={() => addTip(sectionIndex)}>
                                    <FaPlus size={10} /> Add Tip
                                </button>
                            </div>

                            {section.tipsArray?.map((tip, tipIndex) => (
                                <div key={tipIndex} className="pfc-tip-item">
                                    <div className="pfc-tip-input-group">
                                        <label className="pfc-tip-input-label">Content</label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            className="pfc-textarea"
                                            style={{ minHeight: '80px' }}
                                            placeholder="Enter tip content..."
                                            value={tip.text?.[activeLang] || ''}
                                            onChange={(e) => updateTipText(sectionIndex, tipIndex, e.target.value)}
                                        />
                                    </div>
                                    <div className="pfc-tip-input-group">
                                        <label className="pfc-tip-input-label">SVG Icon</label>
                                        <Form.Control
                                            type="text"
                                            className="pfc-input"
                                            placeholder="icon_name"
                                            value={tip.svg || ''}
                                            onChange={(e) => updateTipSvg(sectionIndex, tipIndex, e.target.value)}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '1.5rem' }}>
                                        <button
                                            className="pfc-btn-icon-danger"
                                            onClick={() => removeTip(sectionIndex, tipIndex)}
                                        >
                                            <FaTrash size={12} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}

            {/* Add Section Button (At Bottom) */}
            <button className="pfc-btn-add-section" onClick={addTipSection}>
                <FaPlus /> Add New Tip Section
            </button>
        </div>
    );
};

export default TipsEditor;
